import React, { useEffect, useState } from 'react';
import './App.css';

type DownloadConfig = {
  macArm64: string;
  macX64: string;
  windows: string;
};

function App() {
  const [downloads, setDownloads] = useState<DownloadConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await fetch('/downloads.json', { cache: 'no-store' });
        if (!res.ok) throw new Error('Failed to load downloads config');
        const data: DownloadConfig = await res.json();
        setDownloads(data);
      } catch (e) {
        setError('Download links unavailable. Please try again later.');
      }
    };
    loadConfig();
  }, []);

  const downloadApp = (platform: keyof DownloadConfig) => {
    if (!downloads) {
      alert(error || 'Preparing download links, please try again in a moment.');
      return;
    }

    const url = downloads[platform];
    if (!url || !url.startsWith('http')) {
      alert('Download link not configured.');
      return;
    }

    const link = document.createElement('a');
    link.href = url;
    link.rel = 'noopener noreferrer';
    link.target = '_blank';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="glass fixed top-0 left-0 right-0 z-50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">F</span>
              </div>
              <h1 className="text-2xl font-bold gradient-text">Fathom</h1>
            </div>
            <div className="flex items-center space-x-4">
              <a href="#features" className="btn-secondary">Features</a>
              <a href="#download" className="btn-primary">Download</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="animate-fade-in">
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              Understand <span className="gradient-text">Deeply</span>
              <br />
              Visualize <span className="gradient-text">Clearly</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-4xl mx-auto">
              A powerful data visualization and AI analysis tool that transforms complex data into 
              actionable insights. Built for professionals who demand clarity and precision.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#download" className="btn-primary text-lg px-8 py-4">
                Download for Free
              </a>
              <button className="btn-secondary text-lg px-8 py-4">
                Watch Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Why Choose <span className="gradient-text">Fathom</span>?
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Experience the future of data analysis with cutting-edge AI and intuitive visualizations
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "AI-Powered Analysis",
                description: "Ask questions in plain English and get intelligent insights from your data",
                icon: "🤖"
              },
              {
                title: "Interactive Visualizations",
                description: "Create stunning charts and graphs that bring your data to life",
                icon: "📊"
              },
              {
                title: "Seamless Integration",
                description: "Connect to any database and start analyzing in minutes",
                icon: "🔗"
              }
            ].map((feature, index) => (
              <div key={index} className="card animate-slide-up" style={{ animationDelay: `${index * 0.2}s` }}>
                <div className="text-6xl mb-4">{feature.icon}</div>
                <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
                <p className="text-gray-300">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="download" className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Get <span className="gradient-text">Fathom</span> Today
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Download the desktop app and start transforming your data analysis workflow
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* macOS ARM64 */}
            <div className="download-card animate-slide-up">
              <div className="text-center">
                <div className="download-icon">🍎</div>
                <h3 className="download-title">macOS (Apple Silicon)</h3>
                <p className="download-subtitle">Optimized for M1, M2, M3 Macs</p>
                <div className="installation-steps">
                  <strong>Installation:</strong>
                  <ol>
                    <li>Download the .dmg file</li>
                    <li>Double-click to mount</li>
                    <li>Drag Fathom to Applications</li>
                    <li>Run from Applications folder</li>
                  </ol>
                </div>
                <button 
                  onClick={() => downloadApp('macArm64')}
                  className="btn-primary w-full"
                >
                  Download for Apple Silicon
                </button>
              </div>
            </div>

            {/* macOS x64 */}
            <div className="download-card animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="text-center">
                <div className="download-icon">💻</div>
                <h3 className="download-title">macOS (Intel)</h3>
                <p className="download-subtitle">Compatible with Intel Macs</p>
                <div className="installation-steps">
                  <strong>Installation:</strong>
                  <ol>
                    <li>Download the .dmg file</li>
                    <li>Double-click to mount</li>
                    <li>Drag Fathom to Applications</li>
                    <li>Run from Applications folder</li>
                  </ol>
                </div>
                <button 
                  onClick={() => downloadApp('macX64')}
                  className="btn-primary w-full"
                >
                  Download for Intel
                </button>
              </div>
            </div>

            {/* Windows */}
            <div className="download-card animate-slide-up" style={{ animationDelay: '0.4s' }}>
              <div className="text-center">
                <div className="download-icon">🪟</div>
                <h3 className="download-title">Windows</h3>
                <p className="download-subtitle">Windows 10/11 compatible</p>
                <div className="installation-steps">
                  <strong>Installation:</strong>
                  <ol>
                    <li>Download the .exe file</li>
                    <li>Run the installer</li>
                    <li>Follow the wizard</li>
                    <li>Launch from Start Menu</li>
                  </ol>
                </div>
                <button 
                  onClick={() => downloadApp('windows')}
                  className="btn-primary w-full"
                >
                  Download for Windows
                </button>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-400 text-sm">
              💡 <strong>Direct Downloads:</strong> Links are served from <code>downloads.json</code>. Host files on Google Drive/Cloud and update the IDs only.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="glass mt-16 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400">
            © 2025 Fathom. Built with ❤️ for data professionals.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
